/**
 * Shared hook for loading + mutating training rows of a single
 * `training_type` for a draft. The two training editors (DocsEditor,
 * ExamplesEditor) use this.
 *
 * Keeps the API loose — children mutate via the returned helpers and
 * the hook re-fetches in the background. After a mutation the list is
 * re-fetched WITHOUT toggling `loading`, so the editors keep their rows
 * mounted (focus and unsaved edits in other rows survive). No optimistic
 * updates yet (cheap to add later when the editor latency becomes a
 * problem).
 */

import { useCallback, useEffect, useState } from "react";

import { HttpError } from "@/lib/api";
import {
  trainingApi,
  type TrainingItem,
  type TrainingItemCreate,
  type TrainingItemUpdate,
  type TrainingType,
} from "@/lib/training-api";

interface UseTrainingRows {
  rows: TrainingItem[];
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  create: (payload: Omit<TrainingItemCreate, "training_type">) => Promise<TrainingItem>;
  update: (id: string, payload: TrainingItemUpdate) => Promise<TrainingItem>;
  remove: (id: string) => Promise<void>;
}

export function useTrainingRows(
  draftKey: string,
  type: TrainingType,
): UseTrainingRows {
  const [rows, setRows] = useState<TrainingItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchRows = useCallback(async () => {
    setError(null);
    try {
      const res = await trainingApi.list(draftKey, type);
      setRows(res.items);
    } catch (err) {
      const message = err instanceof HttpError ? err.message : String(err);
      setError(message);
    }
  }, [draftKey, type]);

  const reload = useCallback(async () => {
    if (!draftKey) return;
    setLoading(true);
    try {
      await fetchRows();
    } finally {
      setLoading(false);
    }
  }, [draftKey, fetchRows]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const create = useCallback(
    async (payload: Omit<TrainingItemCreate, "training_type">) => {
      const item = await trainingApi.create(draftKey, {
        ...payload,
        training_type: type,
      });
      await fetchRows();
      return item;
    },
    [draftKey, type, fetchRows],
  );

  const update = useCallback(
    async (id: string, payload: TrainingItemUpdate) => {
      const item = await trainingApi.update(draftKey, id, payload);
      await fetchRows();
      return item;
    },
    [draftKey, fetchRows],
  );

  const remove = useCallback(
    async (id: string) => {
      await trainingApi.remove(draftKey, id);
      await fetchRows();
    },
    [draftKey, fetchRows],
  );

  return { rows, loading, error, reload, create, update, remove };
}
